import _has from "lodash-es/has";
import { FacilityState } from "./types";
import { GetFacilityResponse } from "@api/requests/facility/getFacility";
import { PostFacilityParams } from "@api/requests/facility/postFacility";
import { FacilityValues } from "@initialize/mgr/KEIKAKUSODAN/facility/initialValues";
import {
  INT_FALSE_FROM_API,
  INT_TRUE_FROM_API,
  STRING_FALSE_FROM_API
} from "@constants/variables";
import {
  DEFAULT_FUNCTION_ENHANCED_SYSTEM,
  DEFAULT_NEED_MEDICAL_CARE_HANDICAPPED_CHILD_SUPPORT,
  FACILITY_TARGET_TYPE_KEIKAKUSODAN,
  FACILITY_TARGET_TYPE_SHOGAIJISODAN,
  FACILITY_TYPE_KEIKAKUSODAN
} from "@constants/mgr/KEIKAKUSODAN/variables";
import castNumber from "@utils/dataNormalizer/castNumber";
import { selectDateValueToDate } from "@utils/date";
import { isNil } from "lodash-es";

/**
 * Normalized Type
 */
export type NormalizedGetFacilityResponse = FacilityState;
export type NormalizedPostFacilityParams = FacilityState;

/** GetとPOSTどちらのデータか判定を行う */
const isGetResponse = (
  data: GetFacilityResponse["data"] | PostFacilityParams
): data is GetFacilityResponse["data"] => {
  return _has(data.facility_keikakusodan, "facility_type");
};

/**
 * GetFacilityResponse === PostFacilityParams前提の共通化
 */
const normalizeApiParams = (
  result: GetFacilityResponse["data"] | PostFacilityParams,
  facilityType?: number | null
): FacilityState => {
  const { facility, facility_keikakusodan } = result;
  const defaultValues: Exclude<
    GetFacilityResponse["data"]["facility_keikakusodan"],
    undefined
  > = {
    facility_type: FACILITY_TYPE_KEIKAKUSODAN, // 施設区分: 計画相談単体
    integrated_management_handicapped_child_flg: 0, // 施設（計画相談）：指定障害児相談支援事業所と一体的に運営フラグ
    facilities_keikakusodan_shogaijisodan: []
  };
  const facilityKEIKAKUSODAN = Object.assign(
    defaultValues,
    facility_keikakusodan
  );
  const facilities_keikakusodan_shogaijisodan = facilityKEIKAKUSODAN.facilities_keikakusodan_shogaijisodan.map<
    FacilityState["facilities_keikakusodan_shogaijisodan"][number]
  >((oneFacility) => ({
    target_type: oneFacility.target_type,
    govFacilityNumber: oneFacility.gov_facility_number,
    name: oneFacility.name,
    tel: oneFacility.tel,
    responsiblePerson: oneFacility.responsible_person,
    functionEnhancedSystem: oneFacility.function_enhanced_system
      ? oneFacility.function_enhanced_system.toString()
      : DEFAULT_FUNCTION_ENHANCED_SYSTEM,
    needMedicalCareHandicappedChildSupport202404: oneFacility.need_medical_care_handicapped_child_support_202404
      ? oneFacility.need_medical_care_handicapped_child_support_202404.toString()
      : DEFAULT_NEED_MEDICAL_CARE_HANDICAPPED_CHILD_SUPPORT,
    peerSupportFlg: oneFacility.peer_support_flg === INT_TRUE_FROM_API,
    lifeSupportHubInDistrictFlg:
      oneFacility.life_support_hub_in_district_flg === INT_TRUE_FROM_API,
    lifeSupportFunctionPromotionFlg:
      oneFacility.life_support_function_promotion_flg === INT_TRUE_FROM_API,
    lifeSupportFunctionPromotionTimes: !isNil(
      oneFacility.life_support_function_promotion_times
    )
      ? oneFacility.life_support_function_promotion_times.toString()
      : "",
    informationDisclosureNotReportedFlg:
      oneFacility.information_disclosure_not_reported_flg === INT_TRUE_FROM_API,
    informationDisclosureNotReportedFrom: oneFacility.information_disclosure_not_reported_from
      ? oneFacility.information_disclosure_not_reported_from
      : "",
    informationDisclosureNotReportedTo: oneFacility.information_disclosure_not_reported_to
      ? oneFacility.information_disclosure_not_reported_to
      : "",
    abusePreventionMeasuresNotImplementedFlg:
      oneFacility.abuse_prevention_measures_not_implemented_flg ===
      INT_TRUE_FROM_API,
    abusePreventionMeasuresNotImplementedFrom: oneFacility.abuse_prevention_measures_not_implemented_from
      ? oneFacility.abuse_prevention_measures_not_implemented_from
      : "",
    abusePreventionMeasuresNotImplementedTo: oneFacility.abuse_prevention_measures_not_implemented_to
      ? oneFacility.abuse_prevention_measures_not_implemented_to
      : "",
    workContinuationNotPlanningFlg:
      oneFacility.work_continuation_not_planning_flg === INT_TRUE_FROM_API,
    workContinuationNotPlanningFrom: oneFacility.work_continuation_not_planning_from
      ? oneFacility.work_continuation_not_planning_from
      : "",
    workContinuationNotPlanningTo: oneFacility.work_continuation_not_planning_to
      ? oneFacility.work_continuation_not_planning_to
      : ""
  }));

  return {
    corporationName:
      facility.gov_business_owner !== null ? facility.gov_business_owner : "",
    serviceType: facility.type_service,
    capacity: facility.capacity !== null ? facility.capacity.toString() : "",
    postalCode: facility.postal_code !== null ? facility.postal_code : "",
    selectedPrefectureName: facility.prefecture_name
      ? facility.prefecture_name
      : "NOT_SELECTED",
    selectedCityCode:
      facility.city_id !== null ? facility.city_id.toString() : "NOT_SELECTED",
    restAddress: facility.address !== null ? facility.address : "",
    cityId: facility.city_id !== null ? facility.city_id.toString() : "",
    integratedManagementHandicappedChildFlg:
      facilityKEIKAKUSODAN.integrated_management_handicapped_child_flg ===
      INT_TRUE_FROM_API,
    facilityType:
      isGetResponse(result) && result.facility_keikakusodan
        ? result.facility_keikakusodan.facility_type
        : facilityType || null,
    facilities_keikakusodan_shogaijisodan
  };
};

/**
 * フラグがOFFの時、関連する値を消す
 * 入力後にdisabledとなったテキストエリアのテキストを消す
 */
const removeUnnecessaryValue = (
  target: PostFacilityParams
): PostFacilityParams => {
  const result: PostFacilityParams = {
    facility: { ...target.facility },
    facility_keikakusodan: target.facility_keikakusodan
      ? { ...target.facility_keikakusodan }
      : {}
  };

  return result;
};

// パラメータの親子関係マッピング表
const parentParamsMap = {
  facility: {
    master_subordinate_flg: {
      key: "master_subordinate_flg",
      childKeys: ["master_flg"]
    }
  }
};

/**
 * 子要素の差分判定 子要素に差分がある場合、親要素もパラメータに付与する
 * @param target
 * @param after
 */
const addParentValue = (
  target: PostFacilityParams,
  after: PostFacilityParams
): PostFacilityParams => {
  const result = target;
  Object.keys(parentParamsMap).forEach((facilityKey) => {
    Object.keys(parentParamsMap[facilityKey]).forEach((paramKey) => {
      const childKeys = parentParamsMap[facilityKey][paramKey].childKeys
        ? parentParamsMap[facilityKey][paramKey].childKeys
        : [];
      const isDiffChildren: boolean = childKeys.some((childKey: string) => {
        return result[facilityKey][childKey] !== undefined;
      });
      if (isDiffChildren) {
        result[facilityKey][parentParamsMap[facilityKey][paramKey].key] =
          after[facilityKey][parentParamsMap[facilityKey][paramKey].key];
      }
    });
  });
  return result;
};

export const normalizeGetFacilityResult = (
  response: GetFacilityResponse
): NormalizedGetFacilityResponse => normalizeApiParams(response.data);

export const normalizePostFacilityParams = (
  params: PostFacilityParams,
  facilityType: number | null
): NormalizedPostFacilityParams => normalizeApiParams(params, facilityType);

export const removeNoChangeData = (
  after: PostFacilityParams,
  before: PostFacilityParams,
  isExistKeikaku: boolean,
  isExistShogaiji: boolean
): PostFacilityParams => {
  const facilitiesKeikakusodanShogaijisodan = [];

  if (
    after.facility_keikakusodan &&
    after.facility_keikakusodan.facilities_keikakusodan_shogaijisodan
  ) {
    if (isExistKeikaku) {
      const afterFacilityKeikaku = after.facility_keikakusodan.facilities_keikakusodan_shogaijisodan.find(
        (v) => v.target_type === FACILITY_TARGET_TYPE_KEIKAKUSODAN
      );
      if (afterFacilityKeikaku) {
        facilitiesKeikakusodanShogaijisodan.push({
          target_type: FACILITY_TARGET_TYPE_KEIKAKUSODAN,
          gov_facility_number: afterFacilityKeikaku.gov_facility_number,
          name: afterFacilityKeikaku.name,
          tel: afterFacilityKeikaku.tel,
          responsible_person: afterFacilityKeikaku.responsible_person,
          function_enhanced_system: undefined,
          need_medical_care_handicapped_child_support_202404: undefined,
          peer_support_flg: undefined,
          life_support_hub_in_district_flg: undefined,
          life_support_function_promotion_flg: undefined,
          life_support_function_promotion_times: undefined,
          information_disclosure_not_reported_flg: undefined,
          information_disclosure_not_reported_from: undefined,
          information_disclosure_not_reported_to: undefined,
          abuse_prevention_measures_not_implemented_flg: undefined,
          abuse_prevention_measures_not_implemented_from: undefined,
          abuse_prevention_measures_not_implemented_to: undefined,
          work_continuation_not_planning_flg: undefined,
          work_continuation_not_planning_from: undefined,
          work_continuation_not_planning_to: undefined
        });
      }
    }
    if (isExistShogaiji) {
      const afterFacilityShogaiji = after.facility_keikakusodan.facilities_keikakusodan_shogaijisodan.find(
        (v) => v.target_type === FACILITY_TARGET_TYPE_SHOGAIJISODAN
      );
      if (afterFacilityShogaiji) {
        facilitiesKeikakusodanShogaijisodan.push({
          target_type: FACILITY_TARGET_TYPE_SHOGAIJISODAN,
          gov_facility_number: afterFacilityShogaiji.gov_facility_number,
          name: afterFacilityShogaiji.name,
          tel: afterFacilityShogaiji.tel,
          responsible_person: afterFacilityShogaiji.responsible_person,
          function_enhanced_system: undefined,
          need_medical_care_handicapped_child_support_202404: undefined,
          peer_support_flg: undefined,
          life_support_hub_in_district_flg: undefined,
          life_support_function_promotion_flg: undefined,
          life_support_function_promotion_times: undefined,
          information_disclosure_not_reported_flg: undefined,
          information_disclosure_not_reported_from: undefined,
          information_disclosure_not_reported_to: undefined,
          abuse_prevention_measures_not_implemented_flg: undefined,
          abuse_prevention_measures_not_implemented_from: undefined,
          abuse_prevention_measures_not_implemented_to: undefined,
          work_continuation_not_planning_flg: undefined,
          work_continuation_not_planning_from: undefined,
          work_continuation_not_planning_to: undefined
        });
      }
    }
  }

  const target: PostFacilityParams = {
    facility: {
      gov_business_owner: after.facility.gov_business_owner,
      gov_facility_number: after.facility.gov_facility_number,
      name: after.facility.name,
      type_service: after.facility.type_service,
      responsible_person: after.facility.responsible_person,
      capacity: after.facility.capacity,
      postal_code: after.facility.postal_code,
      prefecture_name: after.facility.prefecture_name,
      city_id: after.facility.city_id,
      address: after.facility.address,
      tel: after.facility.tel,
      multiple_facility_flg: after.facility.multiple_facility_flg,
      users_vs_supporter_grade: after.facility.users_vs_supporter_grade,
      welfare_condition: after.facility.welfare_condition,
      welfare_condition_start_date: after.facility.welfare_condition_start_date,
      welfare_condition_end_date: after.facility.welfare_condition_end_date,
      better_supporter_condition: after.facility.better_supporter_condition,
      lack_of_supporter_flg: after.facility.lack_of_supporter_flg,
      date_start_lack_of_supporter: after.facility.date_start_lack_of_supporter,
      lack_of_service_admin_flg: after.facility.lack_of_service_admin_flg,
      date_start_lack_of_service_admin:
        after.facility.date_start_lack_of_service_admin,
      origin_local_gov_flg: after.facility.origin_local_gov_flg,
      see_hear_team_flg: after.facility.see_hear_team_flg
    },
    facility_keikakusodan: {
      integrated_management_handicapped_child_flg: undefined,
      facilities_keikakusodan_shogaijisodan: facilitiesKeikakusodanShogaijisodan
    }
  };

  Object.keys(target).forEach((key) => {
    Object.keys(target[key]).forEach((param) => {
      if (
        target[key][param] !== undefined &&
        param !== "facilities_keikakusodan_shogaijisodan"
      ) {
        return;
      }

      if (
        param === "facilities_keikakusodan_shogaijisodan" &&
        target.facility_keikakusodan &&
        target.facility_keikakusodan.facilities_keikakusodan_shogaijisodan
      ) {
        target.facility_keikakusodan.facilities_keikakusodan_shogaijisodan.forEach(
          (facility, i) => {
            Object.keys(facility).forEach((flag) => {
              if (target[key][param][i][flag] !== undefined) {
                return;
              }
              if (
                `${before[key][param][i][flag]}` ===
                `${after[key][param][i][flag]}`
              ) {
                target[key][param][i][flag] = undefined;
              } else {
                target[key][param][i][flag] = after[key][param][i][flag];
              }
            });
          }
        );
      } else if (`${before[key][param]}` === `${after[key][param]}`) {
        target[key][param] = undefined;
      } else {
        target[key][param] = after[key][param];
      }
    });
  });

  Object.keys(target).forEach((key) => {
    Object.keys(target[key]).forEach((param) => {
      if (
        param === "facilities_keikakusodan_shogaijisodan" &&
        target.facility_keikakusodan &&
        target.facility_keikakusodan.facilities_keikakusodan_shogaijisodan
      ) {
        target.facility_keikakusodan.facilities_keikakusodan_shogaijisodan.forEach(
          (facility, i) => {
            // 情報公表未報告減算のフラグ、開始終了年月日はセットで送信する
            if (
              facility.information_disclosure_not_reported_flg !== undefined ||
              facility.information_disclosure_not_reported_from !== undefined ||
              facility.information_disclosure_not_reported_to !== undefined
            ) {
              target[key][param][i].information_disclosure_not_reported_flg =
                after[key][param][i].information_disclosure_not_reported_flg;
              target[key][param][i].information_disclosure_not_reported_from =
                after[key][param][i].information_disclosure_not_reported_from;
              target[key][param][i].information_disclosure_not_reported_to =
                after[key][param][i].information_disclosure_not_reported_to;
            }
            // 虐待防止措置未実施減算のフラグ、開始終了年月日はセットで送信する
            if (
              facility.abuse_prevention_measures_not_implemented_flg !==
                undefined ||
              facility.abuse_prevention_measures_not_implemented_from !==
                undefined ||
              facility.abuse_prevention_measures_not_implemented_to !==
                undefined
            ) {
              target[key][param][
                i
              ].abuse_prevention_measures_not_implemented_flg =
                after[key][param][
                  i
                ].abuse_prevention_measures_not_implemented_flg;
              target[key][param][
                i
              ].abuse_prevention_measures_not_implemented_from =
                after[key][param][
                  i
                ].abuse_prevention_measures_not_implemented_from;
              target[key][param][
                i
              ].abuse_prevention_measures_not_implemented_to =
                after[key][param][
                  i
                ].abuse_prevention_measures_not_implemented_to;
            }
            // 業務継続計画未策定のフラグ、開始終了年月日はセットで送信する
            if (
              facility.work_continuation_not_planning_flg !== undefined ||
              facility.work_continuation_not_planning_from !== undefined ||
              facility.work_continuation_not_planning_to !== undefined
            ) {
              target[key][param][i].work_continuation_not_planning_flg =
                after[key][param][i].work_continuation_not_planning_flg;
              target[key][param][i].work_continuation_not_planning_from =
                after[key][param][i].work_continuation_not_planning_from;
              target[key][param][i].work_continuation_not_planning_to =
                after[key][param][i].work_continuation_not_planning_to;
            }
            // 地域生活支援拠点等機能強化加算と算定回数はセットで送信する
            if (
              facility.life_support_function_promotion_flg ||
              facility.life_support_function_promotion_times
            ) {
              target[key][param][i].life_support_function_promotion_flg =
                after[key][param][i].life_support_function_promotion_flg;
              target[key][param][i].life_support_function_promotion_times =
                after[key][param][i].life_support_function_promotion_times;
            }
          }
        );
      }
    });
  });
  return addParentValue(target, after);
};

export const normalizeFormValue = (
  state: FacilityValues,
  isExistKeikaku: boolean,
  isExistShogaiji: boolean
): PostFacilityParams => {
  const facilitiesKeikakusodanShogaijisodan = [];
  if (isExistKeikaku) {
    facilitiesKeikakusodanShogaijisodan.push({
      target_type: FACILITY_TARGET_TYPE_KEIKAKUSODAN,
      gov_facility_number: state.facilityInfoKeikaku.officeNumber,
      name: state.facilityInfoKeikaku.officeName,
      tel: state.facilityInfoKeikaku.tel,
      responsible_person: state.facilityInfoKeikaku.representativeName,
      function_enhanced_system: state.facilityInfoKeikaku.functionEnhancedSystem
        ? parseInt(state.facilityInfoKeikaku.functionEnhancedSystem, 10)
        : parseInt(DEFAULT_FUNCTION_ENHANCED_SYSTEM, 10),
      need_medical_care_handicapped_child_support_202404: state
        .facilityInfoKeikaku.needMedicalCareHandicappedChildSupport202404
        ? parseInt(
            state.facilityInfoKeikaku
              .needMedicalCareHandicappedChildSupport202404,
            10
          )
        : parseInt(DEFAULT_NEED_MEDICAL_CARE_HANDICAPPED_CHILD_SUPPORT, 10),
      peer_support_flg: state.facilityInfoKeikaku.peerSupportFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      life_support_hub_in_district_flg: state.facilityInfoKeikaku
        .lifeSupportHubInDistrictFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      life_support_function_promotion_flg: state.facilityInfoKeikaku
        .lifeSupportFunctionPromotionFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      life_support_function_promotion_times:
        state.facilityInfoKeikaku.lifeSupportHubInDistrictFlg &&
        state.facilityInfoKeikaku.lifeSupportFunctionPromotionTimes
          ? castNumber(
              state.facilityInfoKeikaku.lifeSupportFunctionPromotionTimes
            )
          : null,
      information_disclosure_not_reported_flg: state.facilityInfoKeikaku
        .informationDisclosureNotReportedFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      information_disclosure_not_reported_from:
        state.facilityInfoKeikaku.informationDisclosureNotReportedFrom &&
        selectDateValueToDate(
          state.facilityInfoKeikaku.informationDisclosureNotReportedFrom
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoKeikaku.informationDisclosureNotReportedFrom
            )
          : null,
      information_disclosure_not_reported_to:
        state.facilityInfoKeikaku.informationDisclosureNotReportedTo &&
        selectDateValueToDate(
          state.facilityInfoKeikaku.informationDisclosureNotReportedTo
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoKeikaku.informationDisclosureNotReportedTo
            )
          : null,
      abuse_prevention_measures_not_implemented_flg: state.facilityInfoKeikaku
        .abusePreventionMeasuresNotImplementedFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      abuse_prevention_measures_not_implemented_from:
        state.facilityInfoKeikaku.abusePreventionMeasuresNotImplementedFrom &&
        selectDateValueToDate(
          state.facilityInfoKeikaku.abusePreventionMeasuresNotImplementedFrom
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoKeikaku
                .abusePreventionMeasuresNotImplementedFrom
            )
          : null,
      abuse_prevention_measures_not_implemented_to:
        state.facilityInfoKeikaku.abusePreventionMeasuresNotImplementedTo &&
        selectDateValueToDate(
          state.facilityInfoKeikaku.abusePreventionMeasuresNotImplementedTo
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoKeikaku.abusePreventionMeasuresNotImplementedTo
            )
          : null,
      work_continuation_not_planning_flg: state.facilityInfoKeikaku
        .workContinuationNotPlanningFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      work_continuation_not_planning_from:
        state.facilityInfoKeikaku.workContinuationNotPlanningFrom &&
        selectDateValueToDate(
          state.facilityInfoKeikaku.workContinuationNotPlanningFrom
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoKeikaku.workContinuationNotPlanningFrom
            )
          : null,
      work_continuation_not_planning_to:
        state.facilityInfoKeikaku.workContinuationNotPlanningTo &&
        selectDateValueToDate(
          state.facilityInfoKeikaku.workContinuationNotPlanningTo
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoKeikaku.workContinuationNotPlanningTo
            )
          : null
    });
  }
  if (isExistShogaiji) {
    facilitiesKeikakusodanShogaijisodan.push({
      target_type: FACILITY_TARGET_TYPE_SHOGAIJISODAN,
      gov_facility_number: state.facilityInfoShogaiji.officeNumber,
      name: state.facilityInfoShogaiji.officeName,
      tel: state.facilityInfoShogaiji.tel,
      responsible_person: state.facilityInfoShogaiji.representativeName,
      function_enhanced_system: state.facilityInfoShogaiji
        .functionEnhancedSystem
        ? parseInt(state.facilityInfoShogaiji.functionEnhancedSystem, 10)
        : parseInt(DEFAULT_FUNCTION_ENHANCED_SYSTEM, 10),
      need_medical_care_handicapped_child_support_202404: state
        .facilityInfoShogaiji.needMedicalCareHandicappedChildSupport202404
        ? parseInt(
            state.facilityInfoShogaiji
              .needMedicalCareHandicappedChildSupport202404,
            10
          )
        : parseInt(DEFAULT_NEED_MEDICAL_CARE_HANDICAPPED_CHILD_SUPPORT, 10),
      peer_support_flg: state.facilityInfoShogaiji.peerSupportFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      life_support_hub_in_district_flg: state.facilityInfoShogaiji
        .lifeSupportHubInDistrictFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      life_support_function_promotion_flg: state.facilityInfoShogaiji
        .lifeSupportFunctionPromotionFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      life_support_function_promotion_times:
        state.facilityInfoShogaiji.lifeSupportHubInDistrictFlg &&
        state.facilityInfoShogaiji.lifeSupportFunctionPromotionTimes
          ? castNumber(
              state.facilityInfoShogaiji.lifeSupportFunctionPromotionTimes
            )
          : null,
      information_disclosure_not_reported_flg: state.facilityInfoShogaiji
        .informationDisclosureNotReportedFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      information_disclosure_not_reported_from:
        state.facilityInfoShogaiji.informationDisclosureNotReportedFrom &&
        selectDateValueToDate(
          state.facilityInfoShogaiji.informationDisclosureNotReportedFrom
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoShogaiji.informationDisclosureNotReportedFrom
            )
          : null,
      information_disclosure_not_reported_to:
        state.facilityInfoShogaiji.informationDisclosureNotReportedTo &&
        selectDateValueToDate(
          state.facilityInfoShogaiji.informationDisclosureNotReportedTo
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoShogaiji.informationDisclosureNotReportedTo
            )
          : null,
      abuse_prevention_measures_not_implemented_flg: state.facilityInfoShogaiji
        .abusePreventionMeasuresNotImplementedFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      abuse_prevention_measures_not_implemented_from:
        state.facilityInfoShogaiji.abusePreventionMeasuresNotImplementedFrom &&
        selectDateValueToDate(
          state.facilityInfoShogaiji.abusePreventionMeasuresNotImplementedFrom
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoShogaiji
                .abusePreventionMeasuresNotImplementedFrom
            )
          : null,
      abuse_prevention_measures_not_implemented_to:
        state.facilityInfoShogaiji.abusePreventionMeasuresNotImplementedTo &&
        selectDateValueToDate(
          state.facilityInfoShogaiji.abusePreventionMeasuresNotImplementedTo
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoShogaiji.abusePreventionMeasuresNotImplementedTo
            )
          : null,
      work_continuation_not_planning_flg: state.facilityInfoShogaiji
        .workContinuationNotPlanningFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      work_continuation_not_planning_from:
        state.facilityInfoShogaiji.workContinuationNotPlanningFrom &&
        selectDateValueToDate(
          state.facilityInfoShogaiji.workContinuationNotPlanningFrom
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoShogaiji.workContinuationNotPlanningFrom
            )
          : null,
      work_continuation_not_planning_to:
        state.facilityInfoShogaiji.workContinuationNotPlanningTo &&
        selectDateValueToDate(
          state.facilityInfoShogaiji.workContinuationNotPlanningTo
        ) !== ""
          ? selectDateValueToDate(
              state.facilityInfoShogaiji.workContinuationNotPlanningTo
            )
          : null
    });
  }

  return removeUnnecessaryValue({
    facility: {
      gov_business_owner: state.basic.corporationName,
      type_service: state.basic.serviceType,
      capacity:
        state.basic.capacity !== "" ? parseInt(state.basic.capacity, 10) : null,
      postal_code: state.basic.postalCode,
      prefecture_name: state.basic.prefectureId,
      city_id: parseInt(state.basic.cityId, 10),
      address: state.basic.restAddress,
      users_vs_supporter_grade: "",
      welfare_condition: "",
      welfare_condition_start_date: null,
      welfare_condition_end_date: null,
      better_supporter_condition: "",
      lack_of_supporter_flg: STRING_FALSE_FROM_API,
      date_start_lack_of_supporter: null,
      lack_of_service_admin_flg: STRING_FALSE_FROM_API,
      date_start_lack_of_service_admin: null,
      origin_local_gov_flg: STRING_FALSE_FROM_API,
      see_hear_team_flg: STRING_FALSE_FROM_API
    },
    facility_keikakusodan: {
      facilities_keikakusodan_shogaijisodan: facilitiesKeikakusodanShogaijisodan,
      integrated_management_handicapped_child_flg: state.basic
        .integratedManagementHandicappedChildFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API
    }
  });
};

export const normalizePostDifferenceParams = (
  params: PostFacilityParams,
  response: FacilityValues,
  isExistKeikaku: boolean,
  isExistShogaiji: boolean
): PostFacilityParams => {
  return removeNoChangeData(
    params,
    normalizeFormValue(response, isExistKeikaku, isExistShogaiji),
    isExistKeikaku,
    isExistShogaiji
  );
};
